import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, CreditCard, Palette, Truck, Shield, ChevronRight, Loader2, FileImage } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getIconComponent } from '../data/iconLibrary';
import TemplateCardPreview from '../components/TemplateCardPreview';
import ShopSidebar from '../components/ShopSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopCategory = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [category,   setCategory]   = useState(null);
  const [templates,  setTemplates]  = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [globalPricing, setGlobalPricing] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategoryData();
  }, [categorySlug]);

  const fetchCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch category + templates + all categories + global pricing in parallel
      const [catResponse, templatesResponse, allCatsResponse, pricingResponse] = await Promise.all([
        fetch(`${API_URL}/api/card-designer/categories/by-slug/${categorySlug}`),
        fetch(`${API_URL}/api/card-designer/templates?category_slug=${categorySlug}`),
        fetch(`${API_URL}/api/card-designer/categories`),
        fetch(`${API_URL}/api/shop/global-pricing`),
      ]);

      if (!catResponse.ok) {
        if (catResponse.status === 404) { setError('Category not found'); return; }
        throw new Error('Failed to fetch category');
      }
      setCategory(await catResponse.json());
      if (templatesResponse.ok) setTemplates(await templatesResponse.json());
      if (allCatsResponse.ok)   setAllCategories(await allCatsResponse.json());
      if (pricingResponse.ok)   setGlobalPricing(await pricingResponse.json());
    } catch (err) {
      console.error('Error fetching category data:', err);
      setError(err.message);
      toast({ title: "Error", description: "Failed to load category", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resolveImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_URL}${imageUrl}`;
  };

  // Strip HTML tags from description
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  const handleDesign = (template) => {
    const tSlug = template.slug || template.id;
    window.scrollTo(0, 0);
    navigate(`/shop/${categorySlug}/${tSlug}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  if (error || !category) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Category Not Found</h1>
          <p className="text-gray-600 mb-6">The category you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/shop')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shop
          </Button>
        </div>
      </Layout>
    );
  }

  const subcategories = category.subcategories || [];
  const hasSubcategories = subcategories.length > 0;

  return (
    <Layout>
      <SEO
        title={`${category.name} NFC Business Cards`}
        description={`Buy premium ${category.name.toLowerCase()} NFC business cards online. Customizable designs for ${category.name.toLowerCase()} professionals. Tap to share contacts instantly. Free shipping across India.`}
        keywords={`${category.name.toLowerCase()} NFC cards, ${category.name.toLowerCase()} business cards, ${category.name.toLowerCase()} visiting cards, digital ${category.name.toLowerCase()} cards, NFC cards India`}
        type="website"
        canonicalUrl={`${window.location.origin}/shop/${categorySlug}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name} NFC Business Cards - Piper Card`,
          "description": category.description || `Premium ${category.name} NFC business cards`,
          "url": `${window.location.origin}/shop/${categorySlug}`,
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Shop", "item": `${window.location.origin}/shop` },
              { "@type": "ListItem", "position": 2, "name": category.name, "item": `${window.location.origin}/shop/${categorySlug}` }
            ]
          },
          "mainEntity": {
            "@type": "ItemList",
            "itemListElement": templates.map((t, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": {
                "@type": "Product",
                "name": t.name,
                "description": stripHtml(t.description).slice(0, 200),
                "image": t.front_image ? resolveImageUrl(t.front_image) : undefined,
                "offers": {
                  "@type": "Offer",
                  "price": globalPricing?.nfc_price || 999,
                  "priceCurrency": "INR",
                  "availability": "https://schema.org/InStock",
                  "seller": { "@type": "Organization", "name": "Piper Card" }
                }
              }
            }))
          }
        }}
      />

      <div className="min-h-screen bg-neutral-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/shop" className="hover:text-amber-600 transition-colors">Shop</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-800 font-medium">{category.name}</span>
          </div>

          {/* Category Header */}
          <div className="flex items-start justify-between mb-6 pb-4 border-b border-gray-100">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{category.name}</h1>
              {category.description && (
                <p className="text-gray-500 mt-1 text-sm">{category.description}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">{templates.length} design{templates.length !== 1 ? 's' : ''} available</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 flex-shrink-0">
              <Truck className="w-4 h-4 text-green-600" />
              <span>Free Shipping</span>
            </div>
          </div>

          {/* 2-col layout: main + sidebar */}
          <div className="flex gap-6">

            {/* Main */}
            <div className="flex-1 min-w-0">

              {/* Global NFC Price Banner */}
              {globalPricing && (
                <div className="mb-5 p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                          <path d="M8 12a4 4 0 0 1 8 0"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">All NFC Cards - One Price</p>
                        <p className="text-xs text-gray-500">Choose any design, same price</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                        <p className="text-xs text-gray-400 line-through">₹{globalPricing.nfc_mrp}</p>
                      )}
                      <p className="text-xl font-bold text-violet-600">₹{globalPricing.nfc_price}</p>
                      {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                        <p className="text-[10px] text-emerald-600 font-semibold">
                          {Math.round(((globalPricing.nfc_mrp - globalPricing.nfc_price) / globalPricing.nfc_mrp) * 100)}% OFF
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Subcategories */}
              {hasSubcategories && (
                <div className="mb-8">
                  <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <Palette className="w-4 h-4 text-amber-500" />
                    Subcategories
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {subcategories.map((subcat) => {
                      const SubcatIcon = getIconComponent(subcat.icon);
                      return (
                        <Link key={subcat.id} to={`/shop/${categorySlug}/${subcat.slug}`}
                          className="group bg-white rounded-xl border border-gray-100 p-3 hover:border-amber-200 hover:shadow-sm transition-all"
                        >
                          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center mb-2">
                            <SubcatIcon className="w-4 h-4 text-amber-500" />
                          </div>
                          <p className="font-semibold text-gray-800 text-sm group-hover:text-amber-600 transition-colors leading-tight">{subcat.name}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{subcat.template_count || 0} designs</p>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Templates */}
              <div>
                <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-amber-500" />
                  {hasSubcategories ? 'All Templates' : 'Templates'}
                </h2>

                {templates.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                    <CreditCard className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500">No templates available in this category yet.</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/shop')}>
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back to Shop
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => handleDesign(template)}
                        className="group cursor-pointer bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <div className="relative">
                          {template.thumbnail_image ? (
                            <div className="aspect-[3/4] overflow-hidden">
                              <img
                                src={template.thumbnail_image}
                                alt={template.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          ) : (
                            <div className="aspect-[3/4] bg-gray-50 flex items-center justify-center p-1">
                              <TemplateCardPreview template={template} />
                            </div>
                          )}
                          {/* Hover */}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button size="sm" className="bg-white text-gray-900 hover:bg-amber-50 text-xs">
                              <Palette className="w-3.5 h-3.5 mr-1" /> Customize
                            </Button>
                          </div>
                        </div>
                        <div className="p-2.5">
                          <p className="font-semibold text-gray-900 text-xs line-clamp-1">{template.name}</p>
                          {template.subcategory_name && (
                            <p className="text-[10px] text-amber-600 mt-0.5">{template.subcategory_name}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Trust Badges */}
              <div className="mt-10 flex flex-wrap gap-4 text-xs text-gray-400">
                <div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-green-500" /> Secure Payment</div>
                <div className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-blue-500" /> Free Shipping</div>
                <div className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5 text-amber-500" /> Premium Quality</div>
              </div>

              {/* NFC Smart Cards Info */}
              {globalPricing && (
                <div className="mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                        <path d="M8 12a4 4 0 0 1 8 0"/>
                        <path d="M6 12a6 6 0 0 1 12 0"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">NFC Smart Business Cards</h3>
                      <p className="text-xs text-gray-600">Tap & Share Your Digital Profile Instantly</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-600">All NFC Cards - One Price</p>
                      <p className="text-xs text-gray-500">Premium quality, same great price</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400 line-through">₹{globalPricing.nfc_mrp}</p>
                      <p className="text-xl font-bold text-blue-600">₹{globalPricing.nfc_price}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Tap to Share</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Works with all phones</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">No app needed</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="hidden lg:block w-52 flex-shrink-0">
              <div className="sticky top-4">
                <ShopSidebar categories={allCategories} currentSlug={categorySlug} title="Other Categories" />
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShopCategory;
